import React, { useState, useEffect } from 'react';

/**
 * CookieConsent - GDPR/CCPA Compliant Cookie Consent Banner
 * Designed to match CustomCalendar.jsx styling
 */
const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [consent, setConsent] = useState({
    essential: true,
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    const savedConsent = localStorage.getItem('dizee-cookie-consent');
    if (!savedConsent) {
      setShowBanner(true);
    } else {
      try {
        const parsed = JSON.parse(savedConsent);
        setConsent(parsed);
        applyCookieSettings(parsed);
      } catch (e) {
        console.error('Failed to parse cookie consent:', e);
        setShowBanner(true);
      }
    }
  }, []);

  const applyCookieSettings = (settings) => {
    if (settings.analytics) {
      console.log('Analytics enabled');
    } else {
      console.log('Analytics disabled');
    }

    if (settings.marketing) {
      console.log('Marketing enabled');
    } else {
      console.log('Marketing disabled');
    }
  };

  const acceptAll = () => {
    const allConsent = {
      essential: true,
      analytics: true,
      marketing: true,
      timestamp: new Date().toISOString(),
    };
    setConsent(allConsent);
    localStorage.setItem('dizee-cookie-consent', JSON.stringify(allConsent));
    applyCookieSettings(allConsent);
    setShowBanner(false);
  };

  const acceptEssentialOnly = () => {
    const essentialConsent = {
      essential: true,
      analytics: false,
      marketing: false,
      timestamp: new Date().toISOString(),
    };
    setConsent(essentialConsent);
    localStorage.setItem('dizee-cookie-consent', JSON.stringify(essentialConsent));
    applyCookieSettings(essentialConsent);
    setShowBanner(false);
  };

  const savePreferences = () => {
    const customConsent = {
      ...consent,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem('dizee-cookie-consent', JSON.stringify(customConsent));
    applyCookieSettings(customConsent);
    setShowBanner(false);
  };

  const handleDoNotSell = () => {
    const doNotSellConsent = {
      essential: true,
      analytics: false,
      marketing: false,
      doNotSell: true,
      timestamp: new Date().toISOString(),
    };
    setConsent(doNotSellConsent);
    localStorage.setItem('dizee-cookie-consent', JSON.stringify(doNotSellConsent));
    localStorage.setItem('dizee-do-not-sell', 'true');
    applyCookieSettings(doNotSellConsent);
    setShowBanner(false);
  };

  if (!showBanner) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-[rgba(0,0,0,0.7)]">
      <div
        role="dialog"
        aria-labelledby="cookie-consent-title"
        aria-describedby="cookie-consent-description"
        aria-modal="true"
        className="w-full max-w-[390px]"
      >
      <div className="w-full bg-black border border-[rgba(255,255,255,0.2)] rounded-lg shadow-2xl">
        {/* Header */}
        <div className="p-4 border-b border-[rgba(255,255,255,0.1)]">
          <div className="flex items-start justify-between">
            <div>
              <h2 id="cookie-consent-title" className="text-white text-[14px] font-medium mb-2">
                Cookie Consent
              </h2>
              <p id="cookie-consent-description" className="text-[11px] text-[rgba(255,255,255,0.7)]">
                We use cookies to enhance your experience and analyze site usage.
              </p>
            </div>
            <button
              onClick={acceptEssentialOnly}
              className="p-1 text-[rgba(255,255,255,0.5)] hover:text-white transition-colors rounded focus:outline-none focus:ring-2 focus:ring-[#3b82f6]"
              aria-label="Close and accept essential cookies only"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-4">
          {!showDetails ? (
            <div className="space-y-3">
              <p className="text-[11px] text-[rgba(255,255,255,0.8)]">
                We value your privacy. By clicking "Accept All", you consent to our use of cookies for analytics and marketing purposes. You can customize your preferences or accept only essential cookies.
              </p>
              
              <div className="flex flex-col gap-2">
                <button
                  onClick={acceptAll}
                  className="w-full px-4 py-2.5 bg-[#3b82f6] text-white text-[11px] rounded hover:bg-[#2563eb] transition-colors focus:outline-none focus:ring-4 focus:ring-[rgba(59,130,246,0.3)] font-medium"
                  aria-label="Accept all cookies"
                >
                  Accept All
                </button>
                
                <button
                  onClick={acceptEssentialOnly}
                  className="w-full px-4 py-2.5 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] text-white text-[11px] rounded hover:border-[#3b82f6] hover:bg-[rgba(59,130,246,0.1)] transition-all focus:outline-none focus:ring-4 focus:ring-[rgba(59,130,246,0.3)] font-medium"
                  aria-label="Accept essential cookies only"
                >
                  Essential Only
                </button>
                
                <button
                  onClick={() => setShowDetails(true)}
                  className="w-full px-4 py-2.5 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] text-white text-[11px] rounded hover:border-[#3b82f6] hover:bg-[rgba(59,130,246,0.1)] transition-all focus:outline-none focus:ring-4 focus:ring-[rgba(59,130,246,0.3)] font-medium"
                  aria-label="Customize cookie preferences"
                >
                  Customize
                </button>
              </div>

              {/* CCPA Do Not Sell Link */}
              <div className="pt-3 border-t border-[rgba(255,255,255,0.1)]">
                <button
                  onClick={handleDoNotSell}
                  className="text-[10px] text-[#3b82f6] hover:text-[#60a5fa] transition-colors focus:outline-none focus:ring-2 focus:ring-[#3b82f6] rounded"
                  aria-label="Do not sell my personal information (California residents)"
                >
                  Do Not Sell My Personal Information (CCPA)
                </button>
              </div>

              <div className="text-[10px] text-[rgba(255,255,255,0.5)] pt-2">
                Learn more in our{' '}
                <a href="/privacy-policy" className="text-[#3b82f6] hover:text-[#60a5fa] transition-colors focus:outline-none focus:ring-2 focus:ring-[#3b82f6] rounded">
                  Privacy Policy
                </a>
                {' '}and{' '}
                <a href="/terms-of-service" className="text-[#3b82f6] hover:text-[#60a5fa] transition-colors focus:outline-none focus:ring-2 focus:ring-[#3b82f6] rounded">
                  Terms of Service
                </a>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <h3 className="text-white text-[12px] font-medium">Cookie Preferences</h3>
              
              {/* Essential Cookies */}
              <div className="p-3 bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.1)] rounded">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h4 className="text-white text-[11px] font-medium mb-1">Essential Cookies</h4>
                    <p className="text-[10px] text-[rgba(255,255,255,0.6)]">
                      Required for the website to function properly. Cannot be disabled.
                    </p>
                  </div>
                  <input
                    type="checkbox"
                    checked={true}
                    disabled
                    className="mt-1 w-4 h-4 opacity-50 cursor-not-allowed"
                    aria-label="Essential cookies (always enabled)"
                  />
                </div>
              </div>

              {/* Analytics Cookies */}
              <div className="p-3 bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.1)] rounded">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h4 className="text-white text-[11px] font-medium mb-1">Analytics Cookies</h4>
                    <p className="text-[10px] text-[rgba(255,255,255,0.6)]">
                      Help us understand how visitors interact with our website.
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={consent.analytics}
                      onChange={(e) => setConsent({ ...consent, analytics: e.target.checked })}
                      className="sr-only peer"
                      aria-label="Enable analytics cookies"
                    />
                    <div className="w-10 h-5 bg-[rgba(255,255,255,0.1)] rounded-full peer peer-focus:ring-4 peer-focus:ring-[rgba(59,130,246,0.3)] peer-checked:after:translate-x-5 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-[#3b82f6]"></div>
                  </label>
                </div>
              </div>

              {/* Marketing Cookies */}
              <div className="p-3 bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.1)] rounded">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h4 className="text-white text-[11px] font-medium mb-1">Marketing Cookies</h4>
                    <p className="text-[10px] text-[rgba(255,255,255,0.6)]">
                      Used to track visitors across websites for advertising purposes.
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={consent.marketing}
                      onChange={(e) => setConsent({ ...consent, marketing: e.target.checked })}
                      className="sr-only peer"
                      aria-label="Enable marketing cookies"
                    />
                    <div className="w-10 h-5 bg-[rgba(255,255,255,0.1)] rounded-full peer peer-focus:ring-4 peer-focus:ring-[rgba(59,130,246,0.3)] peer-checked:after:translate-x-5 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-[#3b82f6]"></div>
                  </label>
                </div>
              </div>

              <div className="flex flex-col gap-2 pt-3">
                <button
                  onClick={savePreferences}
                  className="w-full px-4 py-2.5 bg-[#3b82f6] text-white text-[11px] rounded hover:bg-[#2563eb] transition-colors focus:outline-none focus:ring-4 focus:ring-[rgba(59,130,246,0.3)] font-medium"
                  aria-label="Save cookie preferences"
                >
                  Save Preferences
                </button>
                
                <button
                  onClick={() => setShowDetails(false)}
                  className="w-full px-4 py-2.5 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] text-white text-[11px] rounded hover:border-[#3b82f6] hover:bg-[rgba(59,130,246,0.1)] transition-all focus:outline-none focus:ring-4 focus:ring-[rgba(59,130,246,0.3)] font-medium"
                  aria-label="Go back to simple cookie options"
                >
                  Back
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      </div>
    </div>
  );
};

export default CookieConsent;
